// src/components/Layout/Footer.jsx - With Google Sheets
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Linkedin, Instagram, MessageCircle, Send, CheckCircle, AlertCircle } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState(null);

  // YOUR GOOGLE APPS SCRIPT URL (Replace with your actual URL)
  const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbwEwd5-Kj3Z1Ya6juml9_BvhZd7TN_b-QYr9RXnLB085XHe0T4ervzxeb4X7IWRTTYM/exec';

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Our Process', path: '/process' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Certifications', path: '/certifications' },
    { name: 'Contact', path: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
  ];

  // WhatsApp integration
  const phoneNumber = '919940090430';
  const whatsappMessage = 'Hello! I would like to know more about ProfitMix and your sustainable protein solutions.';
  const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  const socialLinks = [
    { 
      icon: Linkedin, 
      href: 'https://www.linkedin.com/in/profit-mix-a23041403/', 
      label: 'LinkedIn',
      color: 'hover:bg-[#0077b5]'
    },
    { 
      icon: Instagram, 
      href: 'https://www.instagram.com/profitmix.in', 
      label: 'Instagram',
      color: 'hover:bg-[#E4405F]'
    },
    { 
      icon: MessageCircle, 
      href: whatsappLink,
      label: 'WhatsApp',
      color: 'hover:bg-[#25D366]',
      isWhatsApp: true
    },
  ];

  // Get user IP (optional but useful)
  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'Unable to fetch';
    }
  };

  // Handle newsletter subscription
  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setSubscribeStatus('error');
      setTimeout(() => setSubscribeStatus(null), 3000);
      return;
    }

    // Basic email validation
    if (!email.includes('@') || !email.includes('.')) {
      setSubscribeStatus('error');
      setTimeout(() => setSubscribeStatus(null), 3000);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get user IP
      const userIP = await getUserIP();
      
      // Prepare data for Google Sheets
      const data = {
        action: 'subscribe',
        email: email,
        source: 'Website Footer - Newsletter',
        ip: userIP,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      };
      
      // Send to Google Sheets
      const response = await fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors', // This prevents CORS issues
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      // Since we're using no-cors, we assume success
      setSubscribeStatus('success');
      setEmail('');
      
      // Optional: Also send to WhatsApp via Telegram webhook
      // You can implement this later
      
    } catch (error) {
      console.error('Subscription error:', error);
      setSubscribeStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubscribeStatus(null), 3000);
    }
  };

  return (
    <footer className="bg-dark-900 border-t border-gold-500/20">
      <div className="container mx-auto px-4 md:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="ProfitMix Logo"
                className="w-12 h-12 object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/logo-placeholder.png';
                }}
              />
              <div>
                <span className="text-2xl font-bold tracking-wider gold-gradient font-playfair">
                  ProFit Mix
                </span>
                <span className="text-xs text-gray-400 tracking-widest uppercase block">
                  NEXT GENERATION PROTEIN
                </span>
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Powering the future of protein through sustainable innovation and industrial-scale production.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full bg-dark-800 border border-gold-500/30 flex items-center justify-center text-gold-400 ${social.color} hover:text-white transition-all duration-300 hover:scale-110 group`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </a>
              ))}
            </div>
            
            {/* WhatsApp Quick Contact */}
            <div className="mt-6 pt-6 border-t border-gold-500/20">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg hover:bg-[#25D366]/20 transition-all duration-300 group"
              >
                <MessageCircle className="w-5 h-5 text-[#25D366] group-hover:scale-110 transition-transform" />
                <span className="text-sm text-gray-300 group-hover:text-white">Chat on WhatsApp</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6 relative inline-block">
              Quick Links
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-gold-400 to-gold-600 rounded-full"></div>
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-gold-400 transition-all duration-300 flex items-center group"
                  >
                    <div className="w-1.5 h-1.5 bg-gold-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-all group-hover:scale-110"></div>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            <div className="mt-6 pt-6 border-t border-gold-500/20">
              <ul className="space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-gray-500 hover:text-gold-400 text-sm transition-colors duration-300 flex items-center group"
                    >
                      <div className="w-1 h-1 bg-gold-500/50 rounded-full mr-3"></div>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6 relative inline-block">
              Contact Info
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-gold-400 to-gold-600 rounded-full"></div>
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 group">
                <Mail className="w-5 h-5 text-gold-400 mt-1 group-hover:scale-110 transition-transform" />
                <div>
                  <p className="text-gray-400 text-sm">Email</p>
                  <a href="mailto:info@profitmix.in" className="text-white hover:text-gold-400 transition-colors text-sm">
                    info@profitmix.in
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3 group">
                <Mail className="w-5 h-5 text-gold-400 mt-1 group-hover:scale-110 transition-transform" />
                <div>
                  <p className="text-gray-400 text-sm">Management</p>
                  <a href="mailto:management@profitmix.in" className="text-white hover:text-gold-400 transition-colors text-sm">
                    management@profitmix.in
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3 group">
                <MapPin className="w-5 h-5 text-gold-400 mt-1 group-hover:scale-110 transition-transform" />
                <div>
                  <p className="text-gray-400 text-sm">Location</p>
                  <p className="text-white text-sm">Chennai, Tamilnadu, India</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Newsletter Section with Google Sheets */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6 relative inline-block">
              Stay Updated
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-gold-400 to-gold-600 rounded-full"></div>
            </h3>
            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
              Subscribe for updates on sustainable protein innovations.
            </p>
            
            <form onSubmit={handleSubscribe} className="space-y-3">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full px-4 py-3 bg-dark-800 border border-gold-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gold-400 hover:text-gold-300 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              {subscribeStatus === 'success' && (
                <div className="flex items-center space-x-2 text-green-500 text-xs animate-fade-in">
                  <CheckCircle className="w-4 h-4" />
                  <span>Thanks for subscribing! 🎉</span>
                </div>
              )}
              
              {subscribeStatus === 'error' && (
                <div className="flex items-center space-x-2 text-red-500 text-xs animate-fade-in">
                  <AlertCircle className="w-4 h-4" />
                  <span>Please enter a valid email address</span>
                </div>
              )}
              
              <p className="text-gray-500 text-xs text-center">
                No spam. Unsubscribe anytime. 🔒
              </p>
            </form>
            
            {/* WhatsApp Subscribe Alternative */}
            <div className="mt-6 pt-4 border-t border-gold-500/20">
              <p className="text-gray-500 text-xs text-center mb-2">
                Or get updates on WhatsApp
              </p>
              <a
                href={`https://wa.me/${phoneNumber}?text=Hi! I want to subscribe to ProfitMix updates`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full space-x-2 px-4 py-2 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg hover:bg-[#25D366]/20 transition-all duration-300 group"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366]" />
                <span className="text-sm text-gray-300 group-hover:text-white">Subscribe on WhatsApp</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gold-500/20 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {currentYear} ProfitMix. All rights reserved. | Designed with 💚 for sustainable future
            </p>
            
            <div className="flex items-center space-x-6">
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-[#25D366] text-sm transition-colors flex items-center space-x-1"
              >
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp</span>
              </a>
              <a 
                href="https://www.linkedin.com/in/profit-mix-a23041403/" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-[#0077b5] text-sm transition-colors"
              >
                LinkedIn
              </a>
              <a 
                href="https://www.instagram.com/profitmix.in" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-[#E4405F] text-sm transition-colors"
              >
                Instagram
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;